import React, { useEffect, useState } from 'react';
import { useImageViewer } from '@ohif/ui-next';
import { API_ENDPOINTS } from '../../../../platform/app/src/custom/config/api.config';
import { fetchAiAnalysisByStudyUid } from '../../../../platform/app/src/custom/services/aiStudyAnalysisService';
import { apiGet } from '../../../../platform/app/src/custom/utils/apiClient';
import { isPatientMode } from '../../../../platform/app/src/custom/utils/patientMode';

type ViewerStudyChipsProps = {
  servicesManager: AppTypes.ServicesManager;
};

/** Treat missing / unusable values as empty so chips stay hidden. */
function usableText(value: unknown): string {
  if (value == null) {
    return '';
  }
  if (typeof value === 'object') {
    return '';
  }
  const text = String(value).trim();
  if (
    !text ||
    text === 'null' ||
    text === 'undefined' ||
    text === '[object Object]' ||
    text === '—' ||
    text === '-'
  ) {
    return '';
  }
  return text;
}

/** QIDO enrichment fields arrive either plain or DICOM-style `{ Value: [...] }`. */
function readPlainField(row: unknown, names: string[]): string {
  if (!row || typeof row !== 'object') {
    return '';
  }
  const record = row as Record<string, unknown>;
  for (const name of names) {
    const value = record[name];
    if (value == null) {
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const text = usableText(value);
      if (text) {
        return text;
      }
      continue;
    }
    if (typeof value === 'object') {
      const dicomValue = (value as { Value?: unknown[] }).Value;
      if (Array.isArray(dicomValue) && dicomValue[0] != null) {
        const text = usableText(dicomValue[0]);
        if (text) {
          return text;
        }
      }
    }
  }
  return '';
}

function unwrapFirstStudy(payload: unknown): unknown {
  if (Array.isArray(payload)) {
    return payload[0];
  }
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.data)) {
    return record.data[0];
  }
  return record.data && typeof record.data === 'object' ? record.data : null;
}

function formatModality(value: string): string {
  const code = usableText(value).toUpperCase();
  if (!code) {
    return '';
  }
  if (['XR', 'CR', 'DX', 'DR'].includes(code)) {
    return 'X-Ray';
  }
  if (code === 'MR') {
    return 'MRI';
  }
  if (code === 'US') {
    return 'USG';
  }
  return code;
}

function formatPriority(value: string): string {
  const raw = usableText(value);
  if (!raw) {
    return '';
  }
  const upper = raw.toUpperCase();
  // Preserve known RIS priority tokens (avoid VIP → Vip)
  if (upper === 'ROUTINE') return 'Routine';
  if (upper === 'URGENT') return 'Urgent';
  if (upper === 'VIP') return 'VIP';
  if (upper === 'MLC') return 'MLC';
  if (upper === 'STAT') return 'STAT';
  if (upper === 'EMERGENCY') return 'Emergency';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

/**
 * Modality / priority / AI classification chips (Sameeksha viewer header).
 * Hide any chip whose value is empty or unavailable (e.g. patient login).
 */
export default function ViewerStudyChips({ servicesManager }: ViewerStudyChipsProps) {
  const { StudyInstanceUIDs } = useImageViewer();
  const studyInstanceUID = StudyInstanceUIDs?.[0] || '';
  const { displaySetService } = servicesManager.services;
  const patientMode = isPatientMode();
  const [modality, setModality] = useState('');
  const [priority, setPriority] = useState('');
  const [classification, setClassification] = useState('');

  useEffect(() => {
    const readModality = () => {
      const sets = displaySetService.getActiveDisplaySets?.() ?? [];
      const matching =
        sets.find(set => set.StudyInstanceUID === studyInstanceUID) || sets[0];
      const instance = matching?.instances?.[0] || matching?.instance;
      setModality(
        formatModality(String(instance?.Modality || matching?.Modality || ''))
      );
    };

    readModality();
    const subscription = displaySetService.subscribe(
      displaySetService.EVENTS.DISPLAY_SETS_ADDED,
      readModality
    );
    return () => subscription.unsubscribe();
  }, [displaySetService, studyInstanceUID]);

  useEffect(() => {
    // Patient portal study APIs do not expose priority / AI enrichment — keep chips hidden.
    if (!studyInstanceUID || patientMode) {
      setPriority('');
      setClassification('');
      return;
    }

    let cancelled = false;
    setPriority('');
    setClassification('');

    Promise.allSettled([
      apiGet<unknown>(API_ENDPOINTS.DICOMWEB.STUDIES_BY_UID(studyInstanceUID)),
      fetchAiAnalysisByStudyUid(studyInstanceUID),
    ]).then(([studyResult, aiResult]) => {
      if (cancelled) {
        return;
      }

      if (studyResult.status === 'fulfilled') {
        const study = unwrapFirstStudy(studyResult.value.data);
        setPriority(
          formatPriority(
            readPlainField(study, [
              'priority',
              'study_priority',
              'studyPriority',
              'requested_procedure_priority',
              'requestedProcedurePriority',
            ])
          )
        );
      } else {
        setPriority('');
      }

      if (aiResult.status === 'fulfilled') {
        const value = usableText(aiResult.value?.classification).toUpperCase();
        setClassification(value === 'NORMAL' || value === 'ABNORMAL' ? value : '');
      } else {
        setClassification('');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [studyInstanceUID, patientMode]);

  const showModality = Boolean(usableText(modality));
  const showPriority = Boolean(usableText(priority));
  const showClassification =
    classification === 'NORMAL' || classification === 'ABNORMAL';

  if (!showModality && !showPriority && !showClassification) {
    return null;
  }

  const baseChip =
    'inline-flex h-6 shrink-0 items-center rounded-full border px-2 text-[11px] font-medium leading-none';

  return (
    <div className="hidden min-w-0 items-center gap-1.5 lg:flex">
      {showModality ? (
        <span className={`${baseChip} border-[#9bd9e7] bg-[#eefbfe] text-[#087f9b]`}>
          {modality}
        </span>
      ) : null}
      {showPriority ? (
        <span className={`${baseChip} border-[#d8dee8] bg-white text-[#475569]`}>
          {priority}
        </span>
      ) : null}
      {showClassification ? (
        <span
          className={`${baseChip} ${
            classification === 'ABNORMAL'
              ? 'border-[#f4a3a3] bg-[#fff1f1] text-[#dc2626]'
              : 'border-[#86d7a2] bg-[#effcf3] text-[#15803d]'
          }`}
        >
          {classification === 'ABNORMAL' ? 'Abnormal' : 'Normal'}
        </span>
      ) : null}
    </div>
  );
}
